import React, { ReactElement } from 'react';

import { Badge } from '@material-ui/core';

import styles from './NotificationBadge.module.scss';

type AnchorOriginClassKeys =
	| 'anchorOriginTopRightRectangle'
	| 'anchorOriginBottomRightRectangle'
	| 'anchorOriginTopLeftRectangle'
	| 'anchorOriginBottomLeftRectangle';
type AnchorOriginClasses = {
	[Property in AnchorOriginClassKeys]?: string;
};

export interface NotificationBadgeProps {
	children: React.ReactNode;
	/* Whether the badge represents online status or notification */
	type: 'notification' | 'onlineStatus';
	verticalPosition: 'top' | 'bottom';
	horizontalPosition: 'left' | 'right';
	/* Whether is displayed on top of circle or rectangular */
	overlap: 'rectangular' | 'circular';
	invisible?: boolean;
	borderless?: boolean;
	badgeContent?: ReactElement;
	/*
		Provides the way how to position custom [badgeContent] based on the combination of
		[verticalPosition], [horizontalPosition] and [overlap] = 'rectangular'.
		See https://material-ui.com/api/badge/#css
	*/
	anchorOriginClasses?: AnchorOriginClasses;
	variant?: 'small' | 'medium';
	borderColor?: 'regular' | 'light';
}

export const NotificationBadge = (props: NotificationBadgeProps) => {
	const position = {
		vertical: props.verticalPosition,
		horizontal: props.horizontalPosition,
	};

	const isDot = props.badgeContent == null;
	const variant = props.variant ?? 'small';
	const borderColor = props.borderColor ?? 'regular';

	const badgeClasses = [
		props.type === 'notification'
			? styles['badge__notification']
			: styles['badge__online-status'],
		...(isDot
			? [
					styles['badge__dot'],
					styles[`badge__dot--${variant}`],
					styles[`badge__dot--${borderColor}`],
			  ]
			: [styles['badge--custom']]),
		props.borderless ? styles['badge--borderless'] : '',
	];

	return (
		<Badge
			anchorOrigin={{ ...position }}
			variant={isDot ? 'dot' : 'standard'}
			overlap={props.overlap}
			classes={{
				badge: badgeClasses.join(' '),
				...props.anchorOriginClasses,
			}}
			invisible={props.invisible}
			badgeContent={props.badgeContent}
		>
			{props.children}
		</Badge>
	);
};
